import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'why-migrate', title: '1. Why Migrate to AI?' },
  { id: 'prepare-content', title: '2. Preparing Your FAQ Content' },
  { id: 'create-chatbot', title: '3. Creating Your AI Assistant' },
  { id: 'import-faq', title: '4. Importing FAQ Content' },
  { id: 'customize-behavior', title: '5. Customizing AI Behavior' },
  { id: 'test-refine', title: '6. Testing & Refinement' },
  { id: 'deploy-measure', title: '7. Deployment & Measurement' },
];

const FaqMigrationPage = () => {
  return (
    <>
      <NextSeo
        title="FAQ to AI Expert Migration Guide"
        description="Learn how to transform your static FAQ page into an intelligent AI assistant that provides personalized, conversational support."
        canonical="https://chat.co/docs/faq-migration"
        openGraph={{
          url: 'https://chat.co/docs/faq-migration',
          title: 'FAQ to AI Expert Migration | Chat.co',
          description:
            'Learn how to transform your static FAQ page into an intelligent AI assistant that provides personalized, conversational support.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Tutorials',
            tags: ['FAQ migration', 'AI assistant', 'customer support', 'chatbot'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - FAQ to AI Migration',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Tutorials"
        categoryColor="text-blue-700"
        title="FAQ to AI Expert Migration"
        description="Transform your static FAQ page into an intelligent AI assistant that handles customer questions naturally and conversationally."
        readingTime="15 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Build a RAG System',
          href: PageLink.docsBuildRagSystem,
        }}
        nextArticle={{
          title: 'Private-App Chatbot with MCP',
          href: PageLink.docsPrivateAppChatbotMcp,
        }}
        keyTakeaways={[
          "AI chatbots understand intent—users don't need exact question matches",
          'Export your FAQ content to a structured format before importing',
          'Group related Q&As into logical categories for better answers',
          'Customize AI behavior with system prompts and guardrails',
          'Measure success: track deflection rates and user satisfaction',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Static FAQ pages are limited. Users have to scroll through dozens of questions hoping to
          find their answer, and when their question isn&apos;t listed exactly, they&apos;re stuck.
          This guide shows you how to migrate your FAQ content into an AI-powered assistant that
          understands intent, handles variations, and provides personalized responses.
        </p>

        {/* Section 1: Why Migrate */}
        <section id="why-migrate" className="scroll-mt-24">
          <h2>1. Why Migrate to AI?</h2>

          <p>
            Before diving into the how, let&apos;s understand the benefits of moving from a
            traditional FAQ page to an AI-powered assistant.
          </p>

          <h3>The Limitations of Static FAQs</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
              <Icon icon="tabler:x" className="mt-0.5 text-lg text-red-500" />
              <div>
                <span className="font-medium text-red-900">Exact match required</span>
                <p className="mt-1 text-sm text-red-800">
                  Users must find the exact question they&apos;re looking for, or they won&apos;t
                  find an answer.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
              <Icon icon="tabler:x" className="mt-0.5 text-lg text-red-500" />
              <div>
                <span className="font-medium text-red-900">No follow-up capability</span>
                <p className="mt-1 text-sm text-red-800">
                  Static pages can&apos;t handle &quot;but what if...&quot; or clarifying questions.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
              <Icon icon="tabler:x" className="mt-0.5 text-lg text-red-500" />
              <div>
                <span className="font-medium text-red-900">Poor discoverability</span>
                <p className="mt-1 text-sm text-red-800">
                  Long FAQ lists become overwhelming. Users give up before finding what they need.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-red-100 bg-red-50 p-4">
              <Icon icon="tabler:x" className="mt-0.5 text-lg text-red-500" />
              <div>
                <span className="font-medium text-red-900">Manual maintenance burden</span>
                <p className="mt-1 text-sm text-red-800">
                  Every new question requires editing HTML or CMS content.
                </p>
              </div>
            </div>
          </div>

          <h3>The AI Advantage</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
              <Icon icon="tabler:check" className="mt-0.5 text-lg text-green-500" />
              <div>
                <span className="font-medium text-green-900">Intent understanding</span>
                <p className="mt-1 text-sm text-green-800">
                  AI understands what users mean, not just what they type. &quot;How do I
                  cancel?&quot; and &quot;I want to stop my subscription&quot; get the same answer.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
              <Icon icon="tabler:check" className="mt-0.5 text-lg text-green-500" />
              <div>
                <span className="font-medium text-green-900">Conversational follow-ups</span>
                <p className="mt-1 text-sm text-green-800">
                  Users can ask clarifying questions, get step-by-step guidance, and have natural
                  back-and-forth conversations.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
              <Icon icon="tabler:check" className="mt-0.5 text-lg text-green-500" />
              <div>
                <span className="font-medium text-green-900">Instant answers</span>
                <p className="mt-1 text-sm text-green-800">
                  No scrolling or searching. Users type a question and get an immediate, relevant
                  response.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-green-100 bg-green-50 p-4">
              <Icon icon="tabler:check" className="mt-0.5 text-lg text-green-500" />
              <div>
                <span className="font-medium text-green-900">Citations & trust</span>
                <p className="mt-1 text-sm text-green-800">
                  AI responses include citations back to your source documents, building trust and
                  credibility.
                </p>
              </div>
            </div>
          </div>

          <div className="my-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
            <h4 className="flex items-center gap-2 font-semibold text-gray-900">
              <Icon icon="tabler:chart-line" className="text-lg text-primary-600" />
              Real Results
            </h4>
            <p className="mt-2 text-sm text-gray-700">
              Companies that migrate from static FAQs to AI assistants typically see:
            </p>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary-600">60%</div>
                <div className="text-xs text-gray-500">Fewer support tickets</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">3x</div>
                <div className="text-xs text-gray-500">More questions answered</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary-600">24/7</div>
                <div className="text-xs text-gray-500">Instant availability</div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Prepare Content */}
        <section id="prepare-content" className="scroll-mt-24">
          <h2>2. Preparing Your FAQ Content</h2>

          <p>
            Good preparation makes migration smoother and results in a more effective AI assistant.
          </p>

          <h3>Audit Your Existing FAQs</h3>
          <p>Start by reviewing your current FAQ content:</p>
          <ol>
            <li>
              <strong>Export all Q&A pairs</strong>
              <p className="mt-1 text-gray-600">
                Gather every question and answer from your FAQ page(s) into a single document.
              </p>
            </li>
            <li>
              <strong>Identify gaps</strong>
              <p className="mt-1 text-gray-600">
                Review support tickets from the last 6 months. What questions do customers ask that
                aren&apos;t covered by your FAQ?
              </p>
            </li>
            <li>
              <strong>Remove outdated content</strong>
              <p className="mt-1 text-gray-600">
                Delete questions about discontinued products, old policies, or features that no
                longer exist.
              </p>
            </li>
            <li>
              <strong>Consolidate duplicates</strong>
              <p className="mt-1 text-gray-600">
                Merge similar questions that have the same answer into a single, comprehensive
                response.
              </p>
            </li>
          </ol>

          <h3>Structure Your Content</h3>
          <p>
            Organize your FAQ content into a clean, structured format. We recommend creating a
            document like this:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`# Company FAQ - Support Assistant

## Account & Billing

### How do I reset my password?
To reset your password:
1. Go to the login page
2. Click "Forgot Password"
3. Enter your email address
4. Check your inbox for a reset link
5. Click the link and create a new password

Password reset links expire after 24 hours.

### How do I cancel my subscription?
You can cancel your subscription anytime from your account settings:
1. Log in to your account
2. Go to Settings > Billing
3. Click "Cancel Subscription"
4. Confirm your cancellation

You'll retain access until the end of your current billing period.
Note: Refunds are available within the first 14 days.

## Product Features

### What file formats do you support?
We support the following file formats:
- Documents: PDF, DOC, DOCX, TXT
- Images: JPG, PNG, GIF
- Spreadsheets: XLS, XLSX, CSV

Maximum file size is 10MB per file.

...`}
            </pre>
          </div>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Tip:</strong> Use clear headings and subheadings. Structure helps the AI
              understand topics and retrieve the most relevant information for each question.
            </p>
          </div>

          <h3>Expand Thin Answers</h3>
          <p>
            One-line answers that worked for static FAQs often need more context for AI. The AI
            performs best when it has complete, self-contained answers.
          </p>

          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Icon icon="tabler:x" className="text-red-500" />
                <span className="text-sm font-medium text-red-900">Before (Too thin)</span>
              </div>
              <p className="text-sm text-red-800">
                <strong>Q: What&apos;s your refund policy?</strong>
                <br />
                A: See our Terms of Service.
              </p>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Icon icon="tabler:check" className="text-green-500" />
                <span className="text-sm font-medium text-green-900">After (Complete)</span>
              </div>
              <p className="text-sm text-green-800">
                <strong>Q: What&apos;s your refund policy?</strong>
                <br />
                A: We offer full refunds within 14 days of purchase. After 14 days, we provide
                prorated refunds for annual plans. To request a refund, contact support@company.com.
              </p>
            </div>
          </div>
        </section>

        {/* Section 3: Create Chatbot */}
        <section id="create-chatbot" className="scroll-mt-24">
          <h2>3. Creating Your AI Assistant</h2>

          <p>Now let&apos;s set up your AI-powered FAQ assistant in Chat.co.</p>

          <h3>Step 1: Create a New Chatbot</h3>
          <ol>
            <li>
              Log in to your{' '}
              <Link href={PageLink.dashboard} className="text-primary-600 hover:underline">
                Chat.co dashboard
              </Link>
            </li>
            <li>
              Click <code>Create Chatbot</code>
            </li>
            <li>
              Enter a name like &quot;Customer Support Assistant&quot; or &quot;Help Center&quot;
            </li>
          </ol>

          <h3>Step 2: Configure Basic Settings</h3>
          <p>Set up your chatbot&apos;s identity:</p>
          <ul>
            <li>
              <strong>Name</strong> — What users will see (e.g., &quot;Support Assistant&quot;)
            </li>
            <li>
              <strong>Welcome Message</strong> — The first message users see. Example: &quot;Hi!
              I&apos;m here to help answer your questions. What would you like to know?&quot;
            </li>
            <li>
              <strong>Avatar</strong> — Upload your company logo or a friendly icon
            </li>
          </ul>

          <h3>Step 3: Set the System Prompt</h3>
          <p>
            The system prompt defines your AI&apos;s personality and behavior. For an FAQ assistant,
            we recommend:
          </p>

          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`You are a helpful customer support assistant for [Company Name].

Guidelines:
- Answer questions based only on the provided knowledge base
- Be friendly, professional, and concise
- If you don't know the answer, say so and suggest contacting support
- Always cite your sources when providing information
- For account-specific questions, direct users to log in or contact support

Contact information:
- Email: support@company.com
- Phone: 1-800-XXX-XXXX
- Hours: Monday-Friday, 9am-5pm EST`}
            </pre>
          </div>
        </section>

        {/* Section 4: Import FAQ */}
        <section id="import-faq" className="scroll-mt-24">
          <h2>4. Importing FAQ Content</h2>

          <p>Now it&apos;s time to add your FAQ content to the knowledge base.</p>

          <h3>Option A: Document Upload</h3>
          <p>If you prepared your FAQ as a structured document (recommended), simply upload it:</p>
          <ol>
            <li>
              Open your bot and go to <code>Knowledge Base</code> → <code>Documents</code>
            </li>
            <li>Drop the file onto the dropzone (or click to browse)</li>
            <li>
              Upload your FAQ document — <code>.pdf</code>, <code>.docx</code>, <code>.csv</code>,{' '}
              <code>.xlsx</code> or <code>.md</code>. Plain <code>.txt</code> is not accepted; paste
              that content into <code>Knowledge Base</code> → <code>Text</code> instead.
            </li>
            <li>Wait for the row to finish processing</li>
          </ol>

          <h3>Option B: Crawl Your FAQ Page</h3>
          <p>If your FAQ is already published online, you can crawl it directly:</p>
          <ol>
            <li>
              Go to <code>Knowledge Base</code> → <code>Links</code>
            </li>
            <li>Enter your FAQ page URL</li>
            <li>
              Choose <code>Single page</code> if it&apos;s one page, <code>Smart</code> if your FAQ
              spans several pages, or <code>Full website</code> to follow every link
            </li>
            <li>
              Start the crawl, then <strong>review the discovered pages</strong> and keep only the
              ones that are really FAQ content — nothing is indexed until you select it
            </li>
          </ol>

          <div className="my-6 rounded-lg border-l-4 border-amber-500 bg-amber-50 p-4">
            <p className="text-sm text-amber-900">
              <strong>Important:</strong> If you crawl your FAQ page, consider disabling the
              original static FAQ or adding a notice directing users to the AI assistant. Having
              both can confuse users.
            </p>
          </div>

          <h3>Option C: Supplement with Additional Content</h3>
          <p>Your FAQ doesn&apos;t have to be the only source. Consider adding:</p>
          <ul>
            <li>Product documentation</li>
            <li>User guides and manuals</li>
            <li>Policy documents (returns, privacy, terms)</li>
            <li>How-to articles from your blog</li>
            <li>Training materials</li>
          </ul>

          <p>More quality content means the AI can answer a wider range of questions accurately.</p>
        </section>

        {/* Section 5: Customize Behavior */}
        <section id="customize-behavior" className="scroll-mt-24">
          <h2>5. Customizing AI Behavior</h2>

          <p>Fine-tune how your AI assistant responds to different situations.</p>

          <h3>Handling Unknown Questions</h3>
          <p>
            Define what happens when the AI doesn&apos;t have information to answer a question. In
            your system prompt, include guidance like:
          </p>
          <div className="my-4 overflow-x-auto rounded-lg border border-gray-200 bg-gray-900 p-4">
            <pre className="text-sm text-gray-300">
              {`If you cannot find relevant information in the knowledge base to answer
a question, respond with:

"I don't have specific information about that in my knowledge base.
For personalized help, please contact our support team at
support@company.com or call 1-800-XXX-XXXX."`}
            </pre>
          </div>

          <h3>Tone and Style</h3>
          <p>Match the AI&apos;s personality to your brand:</p>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Brand Style
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Prompt Guidance
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Professional
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    &quot;Be professional and formal. Use complete sentences. Avoid slang.&quot;
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Friendly
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    &quot;Be warm and conversational. Use a friendly, approachable tone.&quot;
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Technical
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    &quot;Be precise and technical. Include specific details and terminology.&quot;
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Casual
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    &quot;Be casual and relaxed. Short sentences are fine. Feel free to be
                    playful.&quot;
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Suggested Questions</h3>
          <p>
            Help users get started by configuring suggested questions that appear when the chat
            opens. Choose your top 3-4 most common FAQ topics:
          </p>
          <ul>
            <li>&quot;How do I reset my password?&quot;</li>
            <li>&quot;What&apos;s your refund policy?&quot;</li>
            <li>&quot;How do I contact support?&quot;</li>
            <li>&quot;What payment methods do you accept?&quot;</li>
          </ul>
        </section>

        {/* Section 6: Test & Refine */}
        <section id="test-refine" className="scroll-mt-24">
          <h2>6. Testing & Refinement</h2>

          <p>
            Before going live, thoroughly test your AI assistant to ensure it handles questions
            correctly.
          </p>

          <h3>Testing Checklist</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white">
                <span className="text-xs text-gray-400">1</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Test exact FAQ questions</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Copy-paste your original FAQ questions and verify the AI gives correct answers
                  with proper citations.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white">
                <span className="text-xs text-gray-400">2</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Test question variations</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Ask the same question in different ways: &quot;cancel subscription&quot;,
                  &quot;how to cancel&quot;, &quot;stop my plan&quot;, &quot;end membership&quot;.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white">
                <span className="text-xs text-gray-400">3</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Test edge cases</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Ask questions that aren&apos;t in your FAQ. Verify the AI admits it doesn&apos;t
                  know and provides contact information.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white">
                <span className="text-xs text-gray-400">4</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Test follow-up conversations</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Ask a question, then ask a follow-up. &quot;What&apos;s the refund policy?&quot; →
                  &quot;What if it&apos;s been longer than 14 days?&quot;
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded border border-gray-300 bg-white">
                <span className="text-xs text-gray-400">5</span>
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Have others test</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Share the preview link with colleagues. Fresh eyes catch issues you might miss.
                </p>
              </div>
            </div>
          </div>

          <h3>Common Issues and Fixes</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Issue
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Solution
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Wrong or incomplete answers
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Expand the content in your FAQ document with more detail
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Can&apos;t find obvious answers
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Check that the document was processed correctly; try re-uploading
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Too verbose responses
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Add &quot;Be concise. Keep responses under 3 paragraphs.&quot; to system prompt
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Wrong tone
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Adjust the personality instructions in your system prompt
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 font-medium text-gray-900">
                    Makes up information
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Add &quot;Only answer based on the knowledge base. Never invent
                    information.&quot; to prompt
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Section 7: Deploy & Measure */}
        <section id="deploy-measure" className="scroll-mt-24">
          <h2>7. Deployment & Measurement</h2>

          <p>Once testing is complete, deploy your AI assistant and track its performance.</p>

          <h3>Deployment Options</h3>
          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                  <Icon icon="tabler:code" className="text-lg text-blue-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Website Widget</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Add a floating chat widget to your website. Users can click to open and ask
                    questions from any page.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                  <Icon icon="tabler:layout" className="text-lg text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Embedded Chat</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Embed the chat directly into your help center or FAQ page, replacing the static
                    content.
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                  <Icon icon="tabler:link" className="text-lg text-purple-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Standalone Page</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    Link to a full-page chat experience at a custom URL (e.g.,
                    help.yourcompany.com).
                  </p>
                </div>
              </div>
            </div>
          </div>

          <h3>Rollout Strategy</h3>
          <p>We recommend a phased approach:</p>
          <ol>
            <li>
              <strong>Soft launch (Week 1)</strong>
              <p className="mt-1 text-gray-600">
                Deploy to a small section of your site or to internal users only. Monitor for
                issues.
              </p>
            </li>
            <li>
              <strong>Limited release (Week 2)</strong>
              <p className="mt-1 text-gray-600">
                Expand to your help center or FAQ page. Keep the static FAQ available as a fallback.
              </p>
            </li>
            <li>
              <strong>Full deployment (Week 3+)</strong>
              <p className="mt-1 text-gray-600">
                Add the widget site-wide. Consider retiring the static FAQ page.
              </p>
            </li>
          </ol>

          <h3>Measuring Success</h3>
          <p>Track these metrics in your Chat.co dashboard:</p>
          <div className="my-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:messages" className="mx-auto text-2xl text-primary-500" />
              <div className="mt-2 text-sm font-medium text-gray-900">Conversations</div>
              <div className="text-xs text-gray-500">Total chats started</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:thumb-up" className="mx-auto text-2xl text-green-500" />
              <div className="mt-2 text-sm font-medium text-gray-900">Resolution Rate</div>
              <div className="text-xs text-gray-500">Questions answered successfully</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:arrow-down" className="mx-auto text-2xl text-blue-500" />
              <div className="mt-2 text-sm font-medium text-gray-900">Ticket Deflection</div>
              <div className="text-xs text-gray-500">Reduction in support tickets</div>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
              <Icon icon="tabler:star" className="mx-auto text-2xl text-amber-500" />
              <div className="mt-2 text-sm font-medium text-gray-900">User Satisfaction</div>
              <div className="text-xs text-gray-500">Feedback ratings</div>
            </div>
          </div>

          <h3>Continuous Improvement</h3>
          <p>Your AI assistant gets better over time:</p>
          <ul>
            <li>
              <strong>Review conversations weekly</strong> — Look for questions the AI struggles
              with
            </li>
            <li>
              <strong>Expand the knowledge base</strong> — Add content to address gaps
            </li>
            <li>
              <strong>Refine the system prompt</strong> — Adjust based on user feedback
            </li>
            <li>
              <strong>Update content regularly</strong> — Keep information current
            </li>
          </ul>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:rocket" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">Congratulations!</h4>
                <p className="mt-1 text-primary-800">
                  You&apos;ve transformed your static FAQ into an intelligent AI assistant. Your
                  customers can now get instant, accurate answers 24/7, and your support team can
                  focus on more complex issues.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Next Steps</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsWebsiteIntegration}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:plug-connected"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Website Integration</h4>
              <p className="mt-1 text-sm text-gray-600">
                Detailed guide on embedding your chatbot on any website.
              </p>
            </Link>
            <Link
              href={PageLink.docsCitationQuality}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:quote"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Citation Quality Guide</h4>
              <p className="mt-1 text-sm text-gray-600">
                Make your AI&apos;s responses more accurate and trustworthy.
              </p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default FaqMigrationPage;
